import { parseFrontmatter } from '@markbird/shared';
import type { DocMeta } from '../db';
import { matchesQuery, parseQuery } from '../searchQuery';
import { collectTasks, type CollectedTask } from '../tasks';
import { isBuiltinField, type QuerySpec, type SortField } from './parse';

/**
 * Running a query block against the library.
 *
 * Two routes, because they answer different questions well. A query with words
 * in it goes through the search index, which is what makes "every note that
 * mentions invoices" fast in a vault of thousands. A query that only filters —
 * `tag:work`, `path:Projects` — is answered from the metadata projection
 * directly, without waking the index for something it would not speed up.
 *
 * The document the block is written in is never in its own results: a query in
 * "Reading list" that lists "Reading list" is noise, every time.
 */

export interface QueryHit {
  doc: DocMeta;
  /** Rank from the search index, when the query had words to rank by. */
  score: number;
  /** The note's frontmatter, read only when a column asks for a property. */
  props?: Record<string, unknown>;
}

export interface QueryDeps {
  /** Every live document, as metadata. */
  listDocs: () => Promise<DocMeta[]>;
  /** The search index; only consulted when the query has words in it. */
  search: (query: string, limit?: number) => Promise<{ id: string; score: number }[]>;
  /** A document's text. Only read for a task query, and only for the matches. */
  getContent?: (id: string) => Promise<string | undefined>;
  /** Tick a task off in the note it lives in. */
  setTaskChecked?: (docId: string, line: number, checked: boolean, text: string) => Promise<void>;
  /** Write a frontmatter property back into the note. */
  setField?: (docId: string, field: string, value: string) => Promise<void>;
}

function compare(field: SortField, a: QueryHit, b: QueryHit): number {
  switch (field) {
    case 'created':
      return a.doc.createdAt - b.doc.createdAt;
    case 'title':
      return (a.doc.title || '').localeCompare(b.doc.title || '', undefined, {
        sensitivity: 'base',
      });
    case 'relevance':
      return a.score - b.score;
    case 'updated':
    default:
      return a.doc.updatedAt - b.doc.updatedAt;
  }
}

export async function runQuery(
  spec: QuerySpec,
  deps: QueryDeps,
  currentDocId?: string,
): Promise<QueryHit[]> {
  const parsed = parseQuery(spec.search);
  const hasWords = parsed.include.length > 0 || parsed.exclude.length > 0;

  const docs = await deps.listDocs();
  const byId = new Map(docs.map((doc) => [doc.id, doc]));
  let hits: QueryHit[];

  if (hasWords) {
    // The index already applied the whole query, tag and path filters included.
    const found = await deps.search(spec.search, MAX_FROM_INDEX);
    hits = found.flatMap((hit) => {
      const doc = byId.get(hit.id);
      return doc ? [{ doc, score: hit.score }] : [];
    });
  } else {
    hits = docs
      .filter((doc) =>
        matchesQuery(
          { title: doc.title, body: doc.excerpt, tags: doc.tags, folder: doc.folder },
          parsed,
        ),
      )
      .map((doc) => ({ doc, score: 0 }));
  }

  hits = hits.filter((hit) => hit.doc.id !== currentDocId);

  // Relevance only means something when the index ranked them; otherwise the
  // fallback is the one every list in markbird uses.
  const field: SortField =
    spec.sortExplicit && !(spec.sort.field === 'relevance' && !hasWords)
      ? spec.sort.field
      : hasWords
        ? 'relevance'
        : 'updated';
  const descending = spec.sortExplicit ? spec.sort.descending : true;

  hits.sort((a, b) => {
    const order = compare(field, a, b);
    return descending ? -order : order;
  });

  const page = hits.slice(0, spec.limit);
  await attachProperties(page, spec, deps);
  return page;
}

/**
 * Read frontmatter for the rows that will actually be shown, and only when a
 * column asks for a property. A list of titles never needs the text of a note,
 * and a query should not read a vault to render one.
 */
async function attachProperties(hits: QueryHit[], spec: QuerySpec, deps: QueryDeps): Promise<void> {
  const properties = spec.fields.filter((field) => !isBuiltinField(field));
  if (properties.length === 0 || !deps.getContent) return;
  for (const hit of hits) {
    const content = await deps.getContent(hit.doc.id);
    if (content === undefined) continue;
    hit.props = parseFrontmatter(content).data;
  }
}

/** Enough that a limit of 200 can still be sorted differently from the ranking. */
const MAX_FROM_INDEX = 1000;

/**
 * The task lines of the matching notes, rather than the notes.
 *
 * Only the matching documents are read, and only for a task query: a list of
 * notes never needs their text, and a vault should not be loaded into memory to
 * render a bullet list. Ordering puts dated tasks first, soonest first, because
 * that is what an inbox is for; everything else keeps the query's own order.
 */
export async function runTaskQuery(
  spec: QuerySpec,
  deps: QueryDeps,
  currentDocId?: string,
): Promise<CollectedTask[]> {
  const state = spec.tasks ?? 'open';
  // The document limit is generous here: the limit the user wrote counts tasks.
  const docs = await runQuery({ ...spec, limit: MAX_FROM_INDEX }, deps, currentDocId);
  const tasks: CollectedTask[] = [];
  for (const { doc } of docs) {
    const content = await deps.getContent?.(doc.id);
    if (!content) continue;
    tasks.push(...collectTasks({ id: doc.id, title: doc.title, content }, state));
    if (tasks.length >= spec.limit * 4) break;
  }

  tasks.sort((a, b) => {
    if (a.due && b.due) return a.due.localeCompare(b.due);
    if (a.due) return -1;
    if (b.due) return 1;
    return 0;
  });
  return tasks.slice(0, spec.limit);
}
